import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { CardEmptyComponent } from '@shared/components/card';
import { SectionLoaderComponent } from '@shared/components/loader';
import { ModalFormComponent } from '@shared/components/modal/modal-form/modal-form.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { TargetDistributorService } from '../../../target-distributor.service';
import { ITargetDistributorMonitoring__OtherReward, ITargetDistributorMonitoring__OtherRewards } from '../../../target-distributor-monitoring.interface';

/** Payload berbentuk objek, mengikuti `IModalDetailDistributorPayload`. */
export interface IModalHadiahLainnyaPayload {
  targetDistributorId: string;
  distributorId: string;
}

/**
 * [MAI-2725] Sub-modal "Hadiah Didapatkan Distributor".
 *
 * Dibuka dari link "+N Hadiah Lainnya" pada kartu ringkasan di modal Detail per Distributor
 * * (AC-01). Modal ini MENGGANTIKAN modal induk di layar (QA 28 Agu 2026): induknya
 * disembunyikan selagi modal ini terbuka lalu ditampilkan lagi lewat event `closed`,
 * sehingga tombol X tetap mengembalikan pengguna ke modal Detail per Distributor.
 *
 * Judul modal tetap ("Hadiah Didapatkan Distributor", tanpa nama distributor) sesuai design
 * node 8235-51766 — berbeda dari sub-modal Nilai Invoice dan PO yang menyisipkan nama.
 *
 * Semua nominal sudah terformat dari backend; satu-satunya nilai numerik adalah `qty`.
 */
@Component({
  selector: 'app-modal-hadiah-lainnya',
  standalone: true,
  imports: [CommonModule, ModalFormComponent, SectionLoaderComponent, CardEmptyComponent],
  templateUrl: './modal-hadiah-lainnya.component.html',
  styleUrls: ['./modal-hadiah-lainnya.component.scss'],
})
export class ModalHadiahLainnyaComponent {
  @ViewChild('modal') private modal!: ModalFormComponent;

  /**
   * Modal ditutup (X atau backdrop) — pemanggil memakai ini untuk menampilkan kembali
   * modal induk yang disembunyikan saat modal ini dibuka.
   */
  @Output() closed = new EventEmitter<void>();

  readonly STRING_CONSTANTS = STRING_CONSTANTS;

  payload: IModalHadiahLainnyaPayload | null = null;
  summary: ITargetDistributorMonitoring__OtherRewards['summary'] | null = null;
  /** Hasil flatten `data.rewards` (backend mengirimnya nested) — satu blok per tingkat. */
  rewards: ITargetDistributorMonitoring__OtherReward[] = [];
  isLoading = false;
  hasError = false;

  /** `showFooter: false` — design hanya punya tombol close (X) di header (AC-02). */
  modalConfig: ModalConfig = {
    modalTitle: 'Hadiah Didapatkan Distributor',
    showHeader: true,
    showFooter: false,
  };

  /**
   * Lebar 1000px diatur lewat `windowClass` di file scss, sama dengan sub-modal lainnya.
   *
   * Tidak perlu menaikkan z-index terhadap modal induk: induknya disembunyikan selama
   * modal ini terbuka, jadi keduanya tidak pernah tampil bersamaan.
   */
  readonly modalOptions: NgbModalOptions = {
    windowClass: 'modal-hadiah-lainnya',
    scrollable: true,
  };

  constructor(
    private readonly targetDistributorService: TargetDistributorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  /**
   * `detectChanges()` sebelum `open()` wajib: NgbModal baru meng-instantiate `<ng-template>`
   * saat `open()` dipanggil, sehingga judul harus sudah ter-render lebih dulu.
   */
  open(payload: IModalHadiahLainnyaPayload): void {
    this.payload = payload;
    this.summary = null;
    this.rewards = [];
    this.hasError = false;
    this.isLoading = true;
    // NgbModal menandai modal induk `aria-hidden` begitu sub-modal terbuka. Kalau tombol
    // pemicu ("+N Hadiah Lainnya") masih ter-fokus, browser menolak aria-hidden itu dan
    // mencatat warning a11y — jadi fokusnya dilepas dulu.
    (document.activeElement as HTMLElement | null)?.blur();
    this.cdr.detectChanges();
    // `open()` resolve saat modal ditutup/di-dismiss — dipakai untuk memunculkan kembali
    // modal induk, termasuk saat modal ditutup bukan lewat method `close()` di bawah.
    this.modal.open().then(() => this.closed.emit());
    this.fetchOtherRewards();
  }

  close(): void {
    this.modal.close();
  }

  get isEmpty(): boolean {
    return !this.isLoading && !this.hasError && this.rewards.length === 0;
  }

  private fetchOtherRewards(): void {
    if (!this.payload) return;

    this.targetDistributorService
      .getMonitoringOtherRewards(this.payload.targetDistributorId, this.payload.distributorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.applyOtherRewards(res?.data ?? null),
        error: () => this.applyOtherRewards(null),
      });
  }

  private applyOtherRewards(data: ITargetDistributorMonitoring__OtherRewards | null): void {
    this.hasError = !data;
    this.summary = data?.summary ?? null;
    // `rewards` nested (array of array): pengelompokan backend tidak dipakai design, yang
    // dirender adalah satu blok "Hadiah {level}" per hadiah.
    this.rewards = (data?.rewards ?? []).flat();
    this.isLoading = false;
    this.cdr.detectChanges();
  }
}
